import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import branding from '@fingo/lib/theme/fingo/branding';
import { useGetUser, useSnackBars } from '../../hooks';
import { userHasRoutePermission } from '../../helpers/routes';
import isNumber from '../../helpers/numbers';
import { cleanContactType } from '../../helpers';
import { CONTACT_TYPES } from '../../constants/contact-types';
import FormDataGrid from '../dataGrids/FormDataGrid';
import { ArrayOfId, MasterEntityType } from '../../propTypes';
import { CONTACT_COLUMNS, contactTypeToText, getContactTypeSourcesToShow, CONTACT_SOURCES } from '../../constants/contact-columns';
import CONTACTS from '../../graphql/queries/contacts';
import ADD_OR_EDIT_CONTACT from '../../graphql/mutations/add-or-edit-contact';
import ERASE_CONTACT from '../../graphql/mutations/erase-contact';
import { GET_MASTER_ENTITY } from '../../graphql';
import InformationStack from '../stacks/InformationStack';

const ContactsFormGrid = ({
  masterEntity,
  refetchDebtor,
  contactType,
  showAllContactTypes,
  setSelectedContact,
  selectedContacts,
  checkbox,
  columns,
  allContactsWarningMessage,
}) => {
  const [contacts, setContacts] = useState([]);
  const { addAlert } = useSnackBars();
  const masterEntityId = masterEntity.id;
  const setValidSelectedContactIds = useCallback(
    (contactIds) => {
      setSelectedContact(contactIds.filter((id) => isNumber(id)));
    },
    [setSelectedContact],
  );
  const [showAllContactSources, setShowAllContactSources] = useState(false);

  const sourceIn = useMemo(() => (showAllContactSources
    ? CONTACT_SOURCES.ALL
    : getContactTypeSourcesToShow(contactType)), [contactType, showAllContactSources]);

  const {
    loading,
    refetch,
    data: companyContactData,
  } = useQuery(CONTACTS, {
    variables: {
      masterEntity_In: [masterEntityId],
      showHidden: false,
      source_In: sourceIn,
      ...(showAllContactTypes ? {} : { contactType }
      ),
    },
    skip: !masterEntityId,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const contactsData = Object.values(data)[0].edges.map((edge) => edge.node);
      setContacts(contactsData);
    },
  });
  const originalContacts = companyContactData
    ? Object.values(companyContactData)[0].edges.map((edge) => edge.node)
    : [];
  const onSubmit = () => {
    refetch();
    refetchDebtor();
  };
  const onError = (err) => {
    addAlert({
      id: 0,
      color: 'error',
      severity: 'error',
      message: err.message,
    });
  };

  const [createContact] = useMutation(ADD_OR_EDIT_CONTACT, {
    onCompleted: (data) => {
      const createContactResponse = data.createOrEditContact;
      addAlert({
        id: 0,
        color: 'success',
        severity: 'success',
        message: createContactResponse.message,
      });
      onSubmit();
    },
    onError,
    refetchQueries: [GET_MASTER_ENTITY],
  });
  const [updateContact] = useMutation(ADD_OR_EDIT_CONTACT, {
    onCompleted: () => {
      addAlert({
        id: 0,
        color: 'success',
        severity: 'success',
        message: 'Contactos actualizado',
      });
      onSubmit();
    },
    onError,
  });
  const [deleteContact] = useMutation(ERASE_CONTACT, {
    onCompleted: () => {
      addAlert({
        id: 0,
        color: 'primary',
        severity: 'success',
        message: 'Contactos eliminados',
      });
      onSubmit();
    },
    onError,
    refetchQueries: [GET_MASTER_ENTITY],
  });

  const onCreateNewContact = useCallback(
    (row) => {
      const hasSourceColumn = columns.some(
        (column) => column.field === 'source',
      );
      createContact({
        variables: {
          isNew: true,
          company: masterEntityId,
          name: row.name,
          position: row.position,
          phoneNumber: row.phoneNumber,
          email: row.email,
          source: hasSourceColumn ? row.source : CONTACT_SOURCES.PROVIDED_BY_CLIENT,
          contactType: row.contactType ? row.contactType : contactType,
        },
      });
    },
    [createContact],
  );

  const onUpdateContact = useCallback(
    (row) => {
      const updatedContactType = cleanContactType(row.contactType);
      updateContact({
        variables: {
          id: row.id,
          isNew: false,
          company: masterEntityId,
          name: row.name,
          position: row.position,
          phoneNumber: row.phoneNumber,
          email: row.email,
          source: row.source,
          contactType: updatedContactType || contactType,
        },
      });
    },
    [updateContact],
  );

  const onDeleteContact = useCallback(
    (id) => {
      deleteContact({
        variables: {
          id,
          company: masterEntityId,
          contactType,
        },
      });
    },
    [deleteContact],
  );

  const shouldHideFooter = useMemo(() => contacts.length < 100, [contacts]);

  const currentUser = useGetUser();
  const userHasKeyPerm = userHasRoutePermission(currentUser, 'auth.assign_contact_type_commercial_key');

  let newColumns = columns;
  newColumns = [
    ...newColumns,
    {
      field: 'contactType',
      headerName: 'Contacto clave',
      type: 'singleSelect',
      minWidth: 120,
      align: 'center',
      valueOptions: ['PREOFFER_COMMERCIAL_KEY', 'COMMERCIAL'],
      renderCell: ({ row }) => {
        const contactTypes = Array.isArray(row.contactType) ? row.contactType : [];
        const isKeyContact = contactTypes.some(
          (ct) => ct.code === CONTACT_TYPES.PREOFFER_COMMERCIAL_KEY,
        );
        return <Typography>{isKeyContact ? 'Sí' : ''}</Typography>;
      },
      valueFormatter: ({ value }) => contactTypeToText[value],
      editable: true,
      flex: 1,
      hide: !userHasKeyPerm,
    },
  ];

  return (
    <Stack direction="column" gap={1}>
      <Stack direction="row" alignItems="center" gap={1}>
        <Switch
          onChange={(_, checked) => setShowAllContactSources(checked)}
          checked={showAllContactSources}
        />
        <Typography variant="small_2">
          Mostrar todos los contactos
        </Typography>
      </Stack>
      {allContactsWarningMessage && showAllContactSources && (
        <InformationStack
          text={allContactsWarningMessage}
          backgroundColor="warning.A100"
          textColor="warning.A700"
          variant="small_2"
          fontWeight={600}
          IconComponent={<branding.icons.information sx={{ color: 'warning.A700' }} />}
          sx={{ width: 'auto', py: 1 }}
        />
      )}
      <FormDataGrid
        rows={contacts}
        setRows={setContacts}
        columns={newColumns}
        loading={loading}
        boxProps={{
          sx: { height: shouldHideFooter ? 300 : 420, width: '100%' },
        }}
        hideFooter={shouldHideFooter}
        originalRows={originalContacts}
        onCreate={onCreateNewContact}
        onUpdate={onUpdateContact}
        onDelete={onDeleteContact}
        checkboxSelection={checkbox}
        onSelectionModelChange={setValidSelectedContactIds}
        selectionModel={selectedContacts}
      />
    </Stack>
  );
};

ContactsFormGrid.propTypes = {
  masterEntity: MasterEntityType.isRequired,
  refetchDebtor: PropTypes.func,
  contactType: PropTypes.string.isRequired,
  showAllContactTypes: PropTypes.bool.isRequired,
  setSelectedContact: PropTypes.func,
  selectedContacts: ArrayOfId,
  checkbox: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  columns: PropTypes.array,
  allContactsWarningMessage: PropTypes.string,
};

ContactsFormGrid.defaultProps = {
  refetchDebtor: () => null,
  setSelectedContact: () => {},
  selectedContacts: [],
  checkbox: false,
  columns: CONTACT_COLUMNS,
  allContactsWarningMessage: null,
};

export default ContactsFormGrid;
